<template>
  <!-- prettier-ignore -->
  <div
    ref="headerRef"
    class="header"
    :class="{'header--interactive': isInteractive}"
  >
    <PriceWidget.Tabs
      v-if="(personsWithDetails.length > 0 && isInteractive) || !isInteractive"
      :person-id="$props.personId"
      :persons="displayPersons.mutable"
      @open="open()"
      @select="__personId => $emit('select', __personId)"
    />

    <template v-if="isInteractive">
      <the-screen class="header-screen">
        <submit-card class="form-data">
          <template #content>
            <the-typography
              class="header__title"
              type="bodyLargeLong"
              v-text="$t('screen.priceWidget.widget.empty.description')"
            />
          </template>
        </submit-card>
        <screen-contract
          ref="contract"
          :channel="CHANNEL.PRICEWIDGET"
          login-disabled
          :product-id="$props.productId"
          :source="source"
          theme="dark"
          @cancel="cancel"
          @submit="submit"
        />
      </the-screen>
    </template>

    <the-modal
      v-model="show"
      :themes="modalThemes"
      x-only
    >
      <the-screen class="contract__modal">
        <screen-contract
          ref="contract"
          :channel="CHANNEL.PRICEWIDGET"
          :product-id="$props.productId"
          :source="source"
          theme="dark"
          @cancel="cancel"
          @submit="submit"
        />
      </the-screen>
    </the-modal>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'

import { events$, loading$ } from '@/services'

import * as PriceWidget from '@/modules/PriceWidget'

import TheModal from '@/components/Modal/TheModal'
import ScreenContract from '@/components/Screen/ScreenContract'
import TheScreen from '@/components/Container/TheScreen'

import SubmitCard from '@/components/Container/SubmitCard'

import { useLogger, useBasket, usePerson } from '@/hooks'

import { CHANNEL } from '@/config/constants'
import { EVENT_LOGIN } from '@/config/events'

// Hooks
const { logError } = useLogger()
const { getPersonsByMutability, personsWithDetails } = usePerson()
const { basket, updateOvpBasket } = useBasket()

const emit = defineEmits(['select', 'open'])

// INIT
defineProps({
  personId: {
    type: String,
    default: '',
  },

  productId: {
    type: String,
    default: null,
  },

  source: {
    type: String,
    required: true,
  },
})

// DATA
const show = ref(false)
const headerRef = ref(null)

// COMPUTED
const displayPersons = computed(() => {
  return getPersonsByMutability()
})

const isInteractive = computed(() => {
  return personsWithDetails.value.length === 0
})

const modalThemes = computed(() => {
  return ['mobile--dark']
})

// METHODS
function cancel() {
  show.value = false
}

function open() {
  emit('open')

  let tries = 0
  const __open = () => {
    tries++
    if (basket.basketId) {
      show.value = true
    } else if (tries < 10) {
      window.setTimeout(() => __open(), 500)
    } else {
      logError('failed to open modal')
    }
  }

  __open()
}

async function submit(__basket) {
  if (!__basket.channel) __basket.channel = CHANNEL.PRICEWIDGET
  let isScrollTop = false
  loading$.start()

  // FLEX-2891 only scroll to top for initial save
  if (!__basket.createDate) {
    isScrollTop = true
  }

  try {
    await updateOvpBasket(__basket)

    await nextTick()
    loading$.end()
  } catch (error) {
    loading$.failed()
  }

  if (isScrollTop) {
    headerRef.value.scrollIntoView({
      block: 'center',
    })
  }

  cancel()
}

// LIFECYCLE HOOKS
onMounted(() => {
  events$.on(EVENT_LOGIN.OPEN, () => {
    if (show.value) show.value = false
  })
})
</script>

<style name="mobile" scoped>
.header {
  & :deep(.prompt) {
    padding: 16px 0 0 16px;
  }
}
.form-data {
  padding: 16px;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .header-screen :deep(.screen-content) {
    min-width: initial;
    max-width: initial;
    width: 100%;
  }

  .header {
    & :deep(.contract-date__date),
    & :deep(.zipchooser) {
      width: 350px;
    }
  }

  .contract__modal {
    padding: 16px 0 0;
  }
}
</style>
